/**
 * organRegistry.js — КАКОЙ ОРГАН В КАКОМ СЕГМЕНТЕ. [12.0.28]
 *
 * Один реестр на всех, кто размечает:
 *   · «Органы автоматом» — модель внутри программы, 6 / 3 / 1,5 мм
 *     (ai/organsSeg.js — пишет сюда сама после разметки);
 *   · TotalSegmentator и любой импорт маски из .nii (ui/featureBindings.js —
 *     файлы TotalSegmentator названы по структуре: liver.nii.gz);
 *   · органные кнопки 3D (core/organIsolate3D.js) — ЧИТАЮТ отсюда.
 *
 * ПОВОД. Владелец на 12.0.27: органы наконец появились в окне — но кубиками
 * по 6 мм («безобразие, которое нарисовал мне ты»). При этом у него есть
 * «отличная сегментация»: подробная 3 мм, полная 1,5 мм, TotalSegmentator.
 * Кнопки о ней не знали вовсе: у organIsolate3D была своя карта орган →
 * сегмент, заполнявшаяся только его собственными прогонами, и каждая кнопка
 * гнала грубую модель заново. Сегменты в маске — просто номера; какой орган
 * под каким номером, знал лишь тот, кто размечал, и только в ту секунду.
 *
 * ПОПУТНО ИСПРАВЛЕНО.
 *   · Прежняя карта кнопок не сбрасывалась никогда (забытьРазметку никто не
 *     звал): открыл другое исследование — кнопка «Печень» могла показать
 *     под этим именем тот сегмент, где теперь лежит другой орган. Здесь каждая
 *     запись привязана к ИДЕНТИФИКАТОРУ сегментации: сменилась сегментация —
 *     запись не действует.
 *   · Свободный сегмент кнопки считали только по своей карте и могли писать
 *     поверх сегментов панели и TotalSegmentator. Теперь свободный — тот,
 *     которого нет в самой маске.
 *
 * Модуль без импортов: данные и решения. Проверяется verify_12_0_28.
 *
 * EXPORTS: отметитьОрган, записьОргана, забытьОрганы, органИзИмениФайла,
 *          шагМодели, готоваяГодится, модельДляОргана, свободныйСегмент,
 *          модельКнопок, БРЮШНЫЕ, составПрогона, ПЕЧЁНОЧНАЯ
 *
 * [12.0.29] Решение владельца (вариант 1): печень — отдельной моделью
 * liver_unet (срез за срезом в полном разрешении, около минуты); остальные
 * органы — подробной 3 мм, и сразу все брюшные одним прогоном; полная 1,5 мм
 * — только если врач сам выбрал её в панели. Почему не 1,5 мм по умолчанию:
 * каждое КТ у врача — новый человек, реестр живёт одно исследование, и
 * четверть часа ждать пришлось бы на каждом.
 */

/** @type {Map<string, {сегмент:number, источник:string, модель:string|null, шагМм:number|null, segId:string|null}>} */
const _записи = new Map();

/**
 * Запомнить: орган лежит в таком-то сегменте.
 *
 * Сегмент один — орган один: если под этим номером в той же сегментации
 * числился другой орган, его запись снимается. Так бывает, когда панель
 * пишет новую разметку, начиная с активного сегмента, поверх прежней.
 *
 * @param {string} орган — имя как в organsMath.КЛАССЫ (liver, pancreas, …)
 * @param {number} сегмент
 * @param {{источник?:string, модель?:string|null, шагМм?:number|null, segId?:string|null}} [о]
 */
export function отметитьОрган(орган, сегмент, о = {}) {
  const имя = String(орган || '');
  const н = Number(сегмент);
  if (!имя || !Number.isInteger(н) || н < 1) return false;
  const segId = о.segId ?? null;
  for (const [другой, з] of _записи) {
    if (другой !== имя && з.сегмент === н && з.segId === segId) _записи.delete(другой);
  }
  _записи.set(имя, {
    сегмент: н,
    источник: String(о.источник || 'неизвестно'),
    модель: о.модель ?? null,
    шагМм: Number.isFinite(о.шагМм) ? о.шагМм : null,
    segId,
  });
  return true;
}

/**
 * Где лежит орган — только в ЭТОЙ сегментации.
 * @returns {{сегмент:number, источник:string, модель:string|null, шагМм:number|null, segId:string|null}|null}
 */
export function записьОргана(орган, segId) {
  const з = _записи.get(String(орган || ''));
  if (!з) return null;
  return з.segId === (segId ?? null) ? { ...з } : null;
}

/**
 * [12.0.39] Все органы ЭТОЙ сегментации — для показа разметки в 3D.
 * @returns {Array<{орган:string, сегмент:number, источник:string, модель:string|null, шагМм:number|null, segId:string|null}>}
 */
export function органыМаски(segId) {
  const выход = [];
  for (const [орган, з] of _записи) if (з.segId === (segId ?? null)) выход.push({ орган, ...з });
  return выход;
}

/** Забыть всё — например, для проверок. */
export function забытьОрганы() { _записи.clear(); }

/**
 * Орган по имени файла маски: «liver.nii.gz» → «liver». Так называет файлы
 * TotalSegmentator. Имя должно быть известной структурой — иначе null
 * (файл «печень_правка.nii» ничего о своём органе не говорит).
 *
 * @param {string} имяФайла
 * @param {readonly string[]} классы — organsMath.КЛАССЫ
 */
export function органИзИмениФайла(имяФайла, классы) {
  const база = String(имяФайла || '').split(/[\\/]/).pop().replace(/\.nii(\.gz)?$/i, '').trim().toLowerCase();
  if (!база || база === 'background') return null;
  return Array.isArray(классы) && классы.includes(база) ? база : null;
}

/** Шаг сетки модели по её имени: 6, 3 или 1,5 мм. Неизвестная — null. */
export function шагМодели(модель) {
  const м = String(модель || '');
  if (м === 'organs-6mm') return 6;
  if (м === 'organs-3mm') return 3;
  if (/^organs-15mm-\d$/.test(м)) return 1.5;
  return null;
}

/**
 * Годится ли готовая разметка органа.
 *
 * Годится, если в сегменте есть воксели и разметка не ГРУБЕЕ той модели,
 * что врач выбрал в панели. Маска из файла (TotalSegmentator, Slicer) шага
 * не несёт — её считаем годной всегда: её сделали нарочно.
 *
 * Зачем сравнивать шаг. Врач нажал кнопку при «грубой 6 мм», получил кубики,
 * выбрал в панели «полную 1,5 мм» и нажал снова — он хочет подробную форму, а
 * не те же кубики из реестра.
 *
 * @param {object|null} запись — из записьОргана
 * @param {{есть:boolean, шагНужный?:number|null}} о
 */
export function готоваяГодится(запись, { есть, шагНужный } = {}) {
  if (!запись || !есть) return false;
  if (запись.шагМм == null || шагНужный == null) return true;
  return запись.шагМм <= шагНужный + 1e-9;
}

/**
 * Какой моделью считать орган, которого в разметке нет.
 *
 * Той, что выбрана в панели «Органы автоматом», — это решение врача. Если
 * выбранная её не знает (скажем, стоит «полная 1,5 мм — рёбра», а нужна
 * поджелудочная), берётся часть «органы» полной модели, затем подробная 3 мм,
 * и только в последнюю очередь грубая 6 мм — и об этом говорится (замена).
 *
 * @param {string} орган
 * @param {string|null} модельПанели
 * @param {(модель:string, орган:string)=>boolean} знает
 * @returns {{модель:string|null, замена:boolean}}
 */
export function модельДляОргана(орган, модельПанели, знает) {
  const пан = модельПанели || 'organs-6mm';
  if (знает(пан, орган)) return { модель: пан, замена: false };
  for (const м of ['organs-15mm-1', 'organs-3mm', 'organs-6mm']) {
    if (м !== пан && знает(м, орган)) return { модель: м, замена: true };
  }
  return { модель: null, замена: true };
}

/**
 * Первый номер сегмента, которого нет в маске: наибольший занятый + 1.
 * Не «первая дырка»: дырка могла остаться от стёртого органа, и
 * «Вернуть маску» вернула бы его воксели в тот же номер поверх нового.
 *
 * @param {ArrayLike<number>|null} пиксели
 */
export function свободныйСегмент(пиксели) {
  if (!пиксели || !пиксели.length) return 1;
  let наиб = 0;
  for (let i = 0; i < пиксели.length; i++) if (пиксели[i] > наиб) наиб = пиксели[i];
  return наиб + 1;
}

/** [12.0.29] Имя отдельной модели печени (liver_unet.onnx) в реестре. */
export const ПЕЧЁНОЧНАЯ = 'liver-unet';

/**
 * [12.0.29] Какой моделью считают органные кнопки.
 *
 * Полная 1,5 мм — только если врач выбрал её в панели сам. Иначе — подробная
 * 3 мм, даже если в панели стоит «грубая 6 мм» (она там по умолчанию): кубики
 * по 6 мм владелец назвал безобразием, а 3 мм считает минуты, не четверть часа.
 *
 * @param {string|null} модельПанели
 */
export function модельКнопок(модельПанели) {
  return /^organs-15mm-\d$/.test(String(модельПанели || '')) ? модельПанели : 'organs-3mm';
}

/**
 * [12.0.29] Брюшные органы, которые размечаются ОДНИМ прогоном вместе с
 * нужными кнопке. Модель за прогон считает все структуры сразу — время почти
 * не зависит от числа органов (в журнале владельца: одна печень 6 с, селезёнка
 * и две почки 10 с). Значит, раз уж считаем поджелудочную, дешевле сразу
 * разметить и селезёнку с почками: после этого все органные кнопки в этом
 * исследовании мгновенные. Печени здесь нет — у неё своя модель; лёгких нет —
 * их доли врачу в срезах брюшной полости ни к чему, считаются по своей кнопке.
 */
export const БРЮШНЫЕ = Object.freeze(['spleen', 'pancreas', 'kidney_left', 'kidney_right']);

/**
 * [12.0.29] Что просить у модели за один прогон: недостающие органы кнопки
 * плюс брюшные, которых ещё нет в реестре и которые эта модель знает.
 *
 * @param {object} с
 * @param {string[]} с.нехватает — органы кнопки, которых нет
 * @param {(орган:string)=>boolean} с.готов — есть ли годная разметка органа
 * @param {string} с.модель
 * @param {(модель:string, орган:string)=>boolean} с.знает
 * @returns {string[]} без повторов, сперва органы кнопки
 */
export function составПрогона({ нехватает = [], готов = () => false, модель, знает = () => false } = {}) {
  const вышло = [...нехватает];
  for (const б of БРЮШНЫЕ) {
    if (!вышло.includes(б) && !готов(б) && знает(модель, б)) вышло.push(б);
  }
  return вышло;
}
